// Librairies
import { Component, computed, inject, Signal } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { TranslateModule } from '@ngx-translate/core';

// Services
import { SynthesisStore } from 'src/app/services/stores/synthesis.store';

// Interfaces
import { CompanyDashboard, WidgetEditorScope } from 'src/app/interfaces/api/dashboard';

// Components
import { NoDataComponent } from 'projects/ui-library/src/lib/components/no-data/no-data.component';
import { DashboardPanelComponent } from 'src/app/components/project-pages/dashboard/dashboard-panel/dashboard-panel.component';
import { openWidgetCatalog } from 'src/app/components/project-pages/dashboard/dialogs/widget-catalog/widget-catalog.component';
import { ButtonComponent, SpinnerComponent } from 'ui-library';

// New widget on the synthesis dashboard (catalog, then editor), at its end. A close without a result changes nothing.
export function addSynthesisWidget(dialog: MatDialog, store: SynthesisStore): void {
  const scope = store.editorScope();
  if (!scope) return;

  openWidgetCatalog(dialog, { scope, dashboard: store.dashboard() }).subscribe(result => {
    if (result?.companyDashboard) store.setDashboard(result.companyDashboard);
    else if (result) store.reloadDashboard();
  });
}

// Synthesis dashboard of the open theme: loading and empty states; the grid is the project one (dashboard-panel).
@Component({
  selector: 'app-synthesis-dashboard',
  standalone: true,
  imports: [
    TranslateModule,
    ButtonComponent,
    SpinnerComponent,
    NoDataComponent,
    DashboardPanelComponent
  ],
  templateUrl: './synthesis-dashboard.component.html',
  styleUrl: './synthesis-dashboard.component.scss'
})

export class SynthesisDashboardComponent {
  // Services
  readonly store: SynthesisStore = inject(SynthesisStore);
  private readonly dialog: MatDialog = inject(MatDialog);

  // Signals
  readonly dashboard: Signal<CompanyDashboard | null> = this.store.dashboard;
  readonly scope: Signal<WidgetEditorScope | null> = this.store.editorScope;
  readonly canEdit: Signal<boolean> = this.store.canEdit;
  readonly hasCharts: Signal<boolean> = computed(() => !!this.dashboard()?.charts?.length);

  // A new key for each new dashboard object or tenant: the panel is recreated, so its widgets query their data again.
  // Same dashboard object (an open tab again), same key: the panel and its loaded widgets are kept.
  readonly panelKeys: Signal<string[]> = computed(() => [`${this.versionOf(this.dashboard())}|${this.store.tenantId() ?? ''}`]);

  // Variables
  private generation: number = 0;
  private readonly versions = new WeakMap<CompanyDashboard, number>();

  // A number per dashboard object: a new one (loaded, a widget added) recreates the panel.
  private versionOf(dashboard: CompanyDashboard | null): number {
    if (!dashboard) return 0;
    if (!this.versions.has(dashboard)) this.versions.set(dashboard, ++this.generation);
    return this.versions.get(dashboard)!;
  }

  openEditor(): void {
    addSynthesisWidget(this.dialog, this.store);
  }
}
